import React, { useState, useEffect } from 'react'
import posthog from 'posthog-js'
import * as Sentry from '@sentry/react'
import { supabase } from './lib/supabase/config'
import { redeemInviteCode } from './lib/storage'
import { Tracker } from './Tracker.jsx'
import { ErrorBoundary } from './components/ErrorBoundary.jsx'

const INTERNAL_EMAILS = ['ladiigi@gmail.com']

// Google-compliant G mark (standard 4-color, on white bg as required by branding guidelines)
function GoogleG() {
  return (
    <div style={{ width: 20, height: 20, background: '#fff', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
    </div>
  )
}

// ─── Splash ───────────────────────────────────────────────────────────────────
function Splash() {
  const dark = window.matchMedia('(prefers-color-scheme: dark)').matches
  return (
    <div style={{
      minHeight: '100vh', background: dark ? '#080c10' : '#f0f4f8',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16,
      fontFamily: "Georgia,'Times New Roman',serif"
    }}>
      <img src={dark ? '/myronko-wordmark.svg' : '/myronko-wordmark-lite.svg'} alt="MyRonko" style={{ height: 32, width: 'auto' }} />
      <div style={{ color: dark ? 'rgba(0,216,240,0.3)' : 'rgba(0,80,130,0.4)', fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.14em' }}>
        loading...
      </div>
    </div>
  )
}

// ─── Auth Screen ──────────────────────────────────────────────────────────────
function AuthScreen() {
  const [mode, setMode] = useState('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [signUpSent, setSignUpSent] = useState(false)
  const [dm, setDm] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches)

  const fi = {
    outline: 'none', fontFamily: 'inherit',
    background: dm ? 'rgba(0,216,240,0.04)' : '#ffffff',
    border: dm ? '1px solid rgba(0,216,240,0.15)' : '1px solid rgba(0,80,130,0.3)',
    borderRadius: 8, padding: '10px 14px',
    color: dm ? '#d0e8f0' : '#0a1520', fontSize: 14, width: '100%',
    boxSizing: 'border-box', colorScheme: dm ? 'dark' : 'light'
  }

  const handleEmail = async () => {
    if (!email || !password) return setError('Please fill in all fields')
    setLoading(true); setError('')
    try {
      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      } else {
        const { error } = await supabase.auth.signUp({ email, password })
        if (error) throw error
        setSignUpSent(true)
      }
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  const handleGoogle = async () => {
    setLoading(true); setError('')
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      })
      if (error) throw error
    } catch (e) {
      setError(e.message)
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!email) return setError('Enter your email address first.')
    setLoading(true); setError('')
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      })
      if (error) throw error
      setResetSent(true)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  return (
    <div style={{
      minHeight: '100vh', background: dm ? '#080c10' : '#f0f4f8',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "Georgia,'Times New Roman',serif",
      transition: 'background 0.3s'
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img src={dm ? '/myronko-wordmark.svg' : '/myronko-wordmark-lite.svg'} alt="MyRonko" style={{ height: 36, width: 'auto', marginBottom: 10 }} />
          <div style={{ fontSize: 11, color: dm ? 'rgba(0,216,240,0.4)' : '#1e3a52', fontFamily: 'monospace', letterSpacing: '0.2em' }}>
            OWN YOUR PROTOCOL.
          </div>
        </div>

        {signUpSent && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: 28, marginBottom: 16 }}>✉</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: dm ? '#00d8f0' : '#006090', marginBottom: 8 }}>Check your email</div>
            <div style={{ fontSize: 12, color: dm ? 'rgba(0,216,240,0.5)' : 'rgba(0,80,130,0.6)', lineHeight: 1.7, fontFamily: 'monospace' }}>
              We sent a confirmation link to<br />
              <span style={{ color: dm ? '#00d8f0' : '#006090' }}>{email}</span><br /><br />
              Click the link to activate your account.
            </div>
            <button onClick={() => { setSignUpSent(false); setMode('signin'); setError(''); }} style={{
              marginTop: 24, background: 'none', border: `1px solid ${dm ? 'rgba(0,216,240,0.2)' : 'rgba(0,80,130,0.2)'}`,
              color: dm ? 'rgba(0,216,240,0.5)' : 'rgba(0,80,130,0.5)', cursor: 'pointer',
              borderRadius: 20, padding: '6px 16px', fontSize: 10, fontFamily: 'monospace', letterSpacing: '0.1em'
            }}>Back to Sign In</button>
          </div>
        )}

        {!signUpSent && (<>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {['signin', 'signup'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); setResetSent(false); }} style={{
                flex: 1, padding: '8px', borderRadius: 20, cursor: 'pointer',
                fontSize: 11, fontFamily: 'monospace', fontWeight: '600',
                border: `1px solid ${mode === m ? (dm ? 'rgba(0,216,240,0.4)' : 'rgba(0,96,144,0.5)') : (dm ? 'rgba(0,216,240,0.1)' : 'rgba(0,96,144,0.15)')}`,
                background: mode === m ? (dm ? 'rgba(0,216,240,0.1)' : 'rgba(0,96,144,0.1)') : 'transparent',
                color: mode === m ? (dm ? '#00d8f0' : '#006090') : (dm ? '#4a7080' : '#2a4a60')
              }}>
                {m === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8 }}>
            <input style={fi} type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
            <div style={{ position: 'relative' }}>
              <input style={{ ...fi, paddingRight: 40 }} type={showPw ? 'text' : 'password'} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmail()} />
              <button onClick={() => setShowPw(p => !p)} tabIndex={-1} style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                color: dm ? 'rgba(0,216,240,0.4)' : 'rgba(0,80,130,0.5)', fontSize: 12, fontFamily: 'monospace'
              }}>{showPw ? 'hide' : 'show'}</button>
            </div>
          </div>

          {mode === 'signin' && (
            <div style={{ textAlign: 'right', marginBottom: 16 }}>
              {resetSent
                ? <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#4ab868' }}>Reset email sent ✓</span>
                : <button onClick={handleForgotPassword} disabled={loading} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontFamily: 'monospace', color: dm ? 'rgba(0,216,240,0.4)' : 'rgba(0,80,130,0.5)', padding: 0, textDecoration: 'underline' }}>Forgot password?</button>
              }
            </div>
          )}
          {mode === 'signup' && <div style={{ marginBottom: 16 }} />}

          {error && <div style={{ fontSize: 11, color: '#e84040', marginBottom: 12, fontFamily: 'monospace' }}>{error}</div>}

          <button onClick={handleEmail} disabled={loading} style={{
            width: '100%', padding: 13,
            background: dm ? 'rgba(0,216,240,0.1)' : 'rgba(0,96,144,0.08)',
            color: dm ? '#00d8f0' : '#006090',
            fontSize: 14, fontWeight: '700',
            border: dm ? '1px solid rgba(0,216,240,0.3)' : '1px solid rgba(0,96,144,0.4)',
            borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10
          }}>
            {loading ? (mode === 'signin' ? 'Signing in...' : 'Creating account...') : mode === 'signin' ? 'Sign In' : 'Create Account'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 10px' }}>
            <div style={{ flex: 1, height: 1, background: dm ? 'rgba(0,216,240,0.1)' : 'rgba(0,80,130,0.12)' }} />
            <span style={{ fontSize: 10, fontFamily: 'monospace', color: dm ? 'rgba(0,216,240,0.3)' : 'rgba(0,80,130,0.4)' }}>or</span>
            <div style={{ flex: 1, height: 1, background: dm ? 'rgba(0,216,240,0.1)' : 'rgba(0,80,130,0.12)' }} />
          </div>

          <button onClick={handleGoogle} disabled={loading} style={{
            width: '100%', padding: 12,
            background: dm ? '#131314' : '#ffffff',
            color: dm ? '#e3e3e3' : '#1f1f1f',
            fontSize: 13, fontWeight: '600',
            border: dm ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(0,0,0,0.18)',
            borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10
          }}>
            <GoogleG />
            Continue with Google
          </button>
        </>)}

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button onClick={() => setDm(p => !p)} style={{
            background: 'none', border: `1px solid ${dm ? 'rgba(0,216,240,0.2)' : 'rgba(0,80,130,0.2)'}`,
            color: dm ? 'rgba(0,216,240,0.4)' : 'rgba(0,80,130,0.5)',
            cursor: 'pointer', borderRadius: 20, padding: '4px 14px',
            fontSize: 10, fontFamily: 'monospace', letterSpacing: '0.1em'
          }}>{dm ? '☀ Light Mode' : '☾ Dark Mode'}</button>
        </div>

        <div style={{ marginTop: 16, fontSize: 10, color: dm ? 'rgba(0,216,240,0.2)' : 'rgba(0,80,130,0.4)', textAlign: 'center', lineHeight: 1.8, fontFamily: 'monospace' }}>
          By continuing you agree to our{' '}
          <a href="https://myronko.com/terms" target="_blank" style={{ color: dm ? 'rgba(0,216,240,0.4)' : 'rgba(0,80,130,0.6)' }}>Terms</a>
          {' '}and{' '}
          <a href="https://myronko.com/privacy" target="_blank" style={{ color: dm ? 'rgba(0,216,240,0.4)' : 'rgba(0,80,130,0.6)' }}>Privacy Policy</a>.
          MyRonko does not share your data with third parties.
          <br /><br />
          <span style={{ color: dm ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.3)' }}>
            MyRonko is not a medical provider and does not offer medical advice.
            Always consult your provider before making changes to your protocol.
          </span>
        </div>
      </div>
    </div>
  )
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(undefined)
  const [pendingInvite, setPendingInvite] = useState(null)

  useEffect(() => {
    if (import.meta.env.VITE_E2E === 'true') { Promise.resolve().then(() => setUser(null)); return; }

    const params = new URLSearchParams(window.location.search)
    const code = params.get('invite')
    if (code) {
      localStorage.setItem('pendingInvite', code.toUpperCase())
      window.history.replaceState({}, '', window.location.pathname)
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user
      setUser(u ? { ...u, uid: u.id } : null)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user
      if (u) {
        // No email property here on purpose — PostHog's ID alone (an opaque
        // Supabase UUID) is enough to correlate events across sessions/devices
        // for the same user without PostHog ever storing a real email address,
        // matching the privacy policy's "anonymized usage analytics" claim.
        // is_internal is computed from the local session's email either way.
        posthog.identify(u.id, {
          ...(INTERNAL_EMAILS.includes(u.email) && { is_internal: true }),
        })
        Sentry.setUser({ id: u.id, email: u.email })
      } else {
        posthog.reset()
        Sentry.setUser(null)
      }
      setUser(u ? { ...u, uid: u.id } : null)
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!user) return
    const code = localStorage.getItem('pendingInvite')
    if (!code) return
    localStorage.removeItem('pendingInvite')
    redeemInviteCode(code, user.uid).then(inviterUid => {
      if (inviterUid) setPendingInvite({ redeemed: true, inviterUid })
    })
  }, [user])

  if (user === undefined) return <Splash />
  if (!user) return (
    <>
      <AuthScreen />
      {import.meta.env.DEV && (
        <button
          onClick={() => setUser({ uid: 'dev-user' })}
          style={{ position: 'fixed', bottom: 16, right: 16, padding: '6px 14px', background: 'rgba(242,107,59,0.15)', color: '#f26b3b', border: '1px solid rgba(242,107,59,0.4)', borderRadius: 20, fontSize: 10, fontFamily: 'monospace', cursor: 'pointer', zIndex: 999 }}
        >
          dev bypass
        </button>
      )}
    </>
  )
  return <ErrorBoundary><Tracker user={user} pendingInvite={pendingInvite} /></ErrorBoundary>
}
